import { Injectable, Injector } from '@angular/core';
import { toJpeg, toPng } from 'html-to-image';
import { NotificationService } from './notification.service';

export interface ExportOptions {
  scale?: number;
  backgroundColor?: string;
  filename?: string;
  format?: 'png' | 'jpeg';
  quality?: number;
  excludeClassNames?: string[];
  excludeSelectors?: string[];
  filter?: (domNode: Element) => boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ExportService {
  private notificationService: NotificationService;

  constructor(private injector: Injector) {
    this.notificationService = injector.get(NotificationService);
  }

  /**
   * Export an HTML element as an image
   * @param element - The HTML element to export
   * @param options - Export options (filename, scale, format, etc.)
   */
  async exportElementAsImage(element: HTMLElement | null, options: ExportOptions = {}): Promise<void> {
    if (!element) {
      console.error('Element not found for export');
      this.notificationService?.open('Element not found. Please try again.');
      return;
    }

    const {
      scale = 2,
      backgroundColor = '#ffffff',
      filename = 'export.png',
      format = 'jpeg',
      quality = 0.8,
      excludeClassNames = [],
      excludeSelectors = [],
      filter
    } = options;

    const boundingRect = element.getBoundingClientRect();
    const area = boundingRect.width * boundingRect.height;
    const MAX_PIXEL_AREA = 2_000_000; // keep captures lightweight for large tables
    const safePixelRatio = Math.min(
      scale,
      Math.max(0.8, Math.sqrt(MAX_PIXEL_AREA / Math.max(area, 1)))
    );

    const combinedExcludeClasses = Array.from(new Set(['export-ignore', ...excludeClassNames]));
    const combinedExcludeSelectors = excludeSelectors;
    const filterFn = (domNode: Element) => {
      if (filter && !filter(domNode)) {
        return false;
      }
      const element = domNode as Element;
      const classList = element.classList || { contains: () => false };
      if (combinedExcludeClasses.some(cls => classList.contains(cls))) {
        return false;
      }
      if (combinedExcludeSelectors.some(selector => {
        try {
          return element.matches?.(selector);
        } catch {
          return false;
        }
      })) {
        return false;
      }
      return true;
    };

    const exportFn = format === 'jpeg' ? toJpeg : toPng;
    const exportOptions: any = {
      cacheBust: true,
      pixelRatio: safePixelRatio,
      backgroundColor,
      filter: filterFn
    };
    if (format === 'jpeg') {
      exportOptions.quality = quality;
    }

    const originalConsoleError = console.error;
    console.error = (...args: any[]) => {
      const first = args[0];
      if (typeof first === 'string' && first.includes('Error inlining remote css file')) {
        return;
      }
      originalConsoleError(...args);
    };

    try {
      const dataUrl = await exportFn(element, exportOptions);
      this.downloadDataUrl(dataUrl, filename);
    } catch (error) {
      console.error('Error exporting element:', error);
      // fallback with lower pixelRatio
      try {
        const fallbackDataUrl = await exportFn(element, {
          ...exportOptions,
          pixelRatio: 1
        });
        this.downloadDataUrl(fallbackDataUrl, filename);
      } catch (fallbackError) {
        console.error('Fallback export also failed:', fallbackError);
        this.notificationService?.open('Error exporting element. Please try again.');
      }
    } finally {
      console.error = originalConsoleError;
    }
  }

  /**
   * Export an element by ID
   * @param elementId - The ID of the element to export
   * @param options - Export options
   */
  async exportElementById(elementId: string, options: ExportOptions = {}): Promise<void> {
    const element = document.getElementById(elementId);
    await this.exportElementAsImage(element, options);
  }

  /**
   * Export an element by query selector
   * @param selector - CSS selector for the element
   * @param options - Export options
   */
  async exportElementBySelector(selector: string, options: ExportOptions = {}): Promise<void> {
    const element = document.querySelector(selector) as HTMLElement;
    await this.exportElementAsImage(element, options);
  }

  private downloadDataUrl(dataUrl: string, filename: string) {
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataUrl;
    link.click();
  }
}

